import type { Dayjs } from 'dayjs'
import type { SalesOrderListItem } from '@/lib/services/sales-orders/sales-orders.types'

export interface FulfillmentDuePresentationInput {
  fulfillmentDueDate?: SalesOrderListItem['fulfillment_due_date']
  fulfillmentStatus?: SalesOrderListItem['fulfillment_status']
  refundStatus?: SalesOrderListItem['refund_status']
  cancelledAt?: SalesOrderListItem['cancelled_at']
  canShowAttention?: boolean
}

export interface FulfillmentDuePresentation {
  variant: 'ghost' | 'outline' | 'destructive' | 'warning' | 'secondary'
  label: string
}

import dayjs from '@/lib/dayjs'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import { SalesOrderRefundStatus } from '@/lib/enums/SalesOrderRefundStatus'

export function getFulfillmentMethodLabel(fulfillmentType: unknown, fallback = 'Order'): string {
  if (fulfillmentType === FulfillmentType.Delivery.Value) {
    return 'Delivery'
  }

  if (fulfillmentType === FulfillmentType.Pickup.Value) {
    return 'Pickup'
  }

  if (fulfillmentType === FulfillmentType.InStore.Value) {
    return 'In-store pickup'
  }

  if (fulfillmentType === FulfillmentType.Shipping.Value) {
    return 'Shipping order'
  }

  return fallback
}

export function getUnresolvedFulfillmentDateLabel(fulfillmentDate: Dayjs, today: Dayjs): string {
  const daysUntilFulfillment = fulfillmentDate.diff(today, 'day')
  const dateLabel = fulfillmentDate.format('ddd M/D')

  if (daysUntilFulfillment < 0) {
    return `${dateLabel} · ${Math.abs(daysUntilFulfillment)}d ago`
  }

  if (daysUntilFulfillment === 0) {
    return `${dateLabel} · today`
  }

  return `${dateLabel} · in ${daysUntilFulfillment}d`
}

function getFulfillmentCalendarDate(fulfillmentDueDate: string): Dayjs {
  // Fulfillment due dates are calendar days; keep the API date portion before org-time parsing.
  const dateOnly = String(fulfillmentDueDate ?? '').match(/^(\d{4}-\d{2}-\d{2})/)?.[1]

  return dayjs(dateOnly ?? dayjs.utc(fulfillmentDueDate).format('YYYY-MM-DD')).startOf('day')
}

export function getFulfillmentDuePresentation({
  fulfillmentDueDate,
  fulfillmentStatus,
  refundStatus,
  cancelledAt,
  canShowAttention = true,
}: FulfillmentDuePresentationInput): FulfillmentDuePresentation | null {
  if (!fulfillmentDueDate) {
    return null
  }

  const today = dayjs().startOf('day')
  const fulfillmentDate = getFulfillmentCalendarDate(fulfillmentDueDate)
  const isCancelled = Boolean(cancelledAt)
  const isRefunded = refundStatus === SalesOrderRefundStatus.Refunded.Value
  const isFulfilled = fulfillmentStatus === SalesOrderFulfillmentStatus.Fulfilled.Value
  const isLateForFulfillment = fulfillmentDate.isBefore(today, 'day')
  const isDueToday = fulfillmentDate.isSame(today, 'day')
  const isDueTomorrow = fulfillmentDate.isSame(today.add(1, 'day'), 'day')

  if (!canShowAttention) {
    return {
      variant: !isCancelled && !isRefunded && isLateForFulfillment ? 'ghost' : 'outline',
      label: fulfillmentDate.format('ddd M/D'),
    }
  }

  if (!isCancelled && !isRefunded && !isFulfilled && isLateForFulfillment) {
    return {
      variant: 'destructive',
      label: getUnresolvedFulfillmentDateLabel(fulfillmentDate, today),
    }
  }

  if (!isCancelled && !isRefunded && isLateForFulfillment) {
    return {
      variant: 'ghost',
      label: fulfillmentDate.format('ddd M/D'),
    }
  }

  if (!isCancelled && !isRefunded && !isFulfilled && isDueToday) {
    return {
      variant: 'warning',
      label: getUnresolvedFulfillmentDateLabel(fulfillmentDate, today),
    }
  }

  if (!isCancelled && !isRefunded && !isFulfilled && isDueTomorrow) {
    return {
      variant: 'secondary',
      label: getUnresolvedFulfillmentDateLabel(fulfillmentDate, today),
    }
  }

  if (!isCancelled && !isRefunded && !isFulfilled) {
    return {
      variant: 'outline',
      label: getUnresolvedFulfillmentDateLabel(fulfillmentDate, today),
    }
  }

  return {
    variant: 'outline',
    label: fulfillmentDate.format('ddd M/D'),
  }
}
